import type { AgentWeekInput, ExerciseFeedback, SetEntry, TodayPayload, WorkoutSession } from "./shared";

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string, public details?: unknown) { super(message); }
}

async function requestFingerprint(method: string, path: string, body: BodyInit | null | undefined): Promise<string> {
  const value = `${method}:${path}:${typeof body === "string" ? body : ""}`;
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return `${method}:${path}:${Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function pendingMutationKey(storageKey: string): { key: string; clear: () => void } {
  try {
    const existing = sessionStorage.getItem(storageKey);
    const key = existing ?? crypto.randomUUID();
    if (!existing) sessionStorage.setItem(storageKey, key);
    return { key, clear: () => sessionStorage.removeItem(storageKey) };
  } catch {
    return { key: crypto.randomUUID(), clear: () => undefined };
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const retryable = (method === "POST" && (path === "/api/sessions" || /^\/api\/sessions\/[^/]+\/sets$/.test(path) || /^\/api\/plan\/drafts\/[^/]+\/publish$/.test(path)))
    || (method === "PATCH" && (/^\/api\/sessions\/[^/]+$/.test(path) || /^\/api\/agent\/weeks\/[^/]+\/coaching$/.test(path)))
    || (method === "PUT" && (path === "/api/agent/weeks" || path === "/api/plan/drafts" || /^\/api\/plan\/drafts\/[^/]+$/.test(path) || /^\/api\/sessions\/[^/]+\/exercise-notes\/[^/]+$/.test(path)));
  const pendingStorageKey = retryable && !headers.has("Idempotency-Key")
    ? `superset:pending:${await requestFingerprint(method, path, init.body)}` : null;
  const pending = pendingStorageKey ? pendingMutationKey(pendingStorageKey) : null;
  if (pending) headers.set("Idempotency-Key", pending.key);
  let response: Response;
  try {
    response = await fetch(path, { ...init, credentials: "include", headers });
  } catch (caught) {
    // A mutation may have reached the server before the connection dropped;
    // retry with the same key so the server can return its idempotent result.
    if (!retryable) throw caught;
    response = await fetch(path, { ...init, credentials: "include", headers });
  }
  let payload: { error?: string; code?: string; details?: unknown } & T;
  try { payload = await response.json() as { error?: string; code?: string; details?: unknown } & T; }
  catch { throw new ApiError("Unexpected server response", response.status); }
  if (!response.ok) throw new ApiError(payload.error ?? `Request failed (${response.status})`, response.status, payload.code, payload.details);
  pending?.clear();
  return payload;
}

export type PlanPayload = {
  weeks: Array<{ id: string; week_start: string; title: string; focus: string | null; coach_notes: string | null; warmup: string[]; source: string; revision?: number }>;
  days: Array<{ id: string; routine_week_id: string; day_date: string; title: string; focus: string | null; estimated_minutes: number | null; position: number; session_status: "planned" | "active" | "completed" }>;
  blocks: Array<{ id: string; routine_day_id: string; label: string; kind: string; rounds: number; rest_seconds: number; notes: string | null; position: number; exercise_names: string }>;
  active_session?: WorkoutSession | null;
};

export type ReportsPayload = {
  summary: { sessions: number; sets: number; volume: number; avg_effort: number };
  weekly: Array<{ week: string; sessions: number; sets: number; volume: number }>;
  exercises: Array<{ id: string; name: string; sets: number; best_weight: number | null; estimated_1rm: number | null; last_trained: string }>;
  recent: Array<{ id: string; title: string; started_at: string; completed_at: string; perceived_effort: number | null; sets: number; volume: number }>;
};

export type PlanDraft = {
  id: string;
  week_start: string;
  title: string;
  status: "draft" | "published" | "archived";
  created_at: string;
  updated_at: string;
  published_at: string | null;
  revision?: number;
  payload: AgentWeekInput;
};

export type ApiToken = {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
};

export type AccountPayload = {
  ok: true;
  role: "user" | "agent";
  auth_provider: "better-auth" | "agent-token";
  username: string;
  user: { id: string; name: string; email: string };
  athlete: {
    id: string;
    membership_role: "owner" | "coach" | "viewer";
    display_name: string;
    timezone: string;
    unit_system: "lb" | "kg";
  };
  onboarding: {
    completed: boolean;
    completed_at: string | null;
    training_days_per_week: number | null;
    session_minutes: number | null;
    experience_level: "beginner" | "intermediate" | "advanced" | null;
    goals: string;
    available_equipment: string;
    movement_constraints: string;
    variety_preference: "steady" | "balanced" | "frequent";
  };
};

export type OnboardingInput = {
  display_name: string;
  timezone: string;
  unit_system: "lb" | "kg";
  training_days_per_week: number;
  session_minutes: number;
  experience_level: "beginner" | "intermediate" | "advanced";
  goals: string;
  available_equipment: string;
  movement_constraints: string;
  variety_preference: "steady" | "balanced" | "frequent";
};

export const api = {
  check: () => request<AccountPayload>("/api/me"),
  saveOnboarding: (input: OnboardingInput) => request<AccountPayload>("/api/onboarding", { method: "PUT", body: JSON.stringify(input) }),
  today: () => {
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    return request<TodayPayload>(`/api/today?date=${localDate}`);
  },
  startSession: (routineDayId: string) => request<{ session: WorkoutSession }>("/api/sessions", { method: "POST", body: JSON.stringify({ routine_day_id: routineDayId }) }),
  logSet: async (sessionId: string, value: { routine_item_id: string; exercise_id: string; round_number: number; weight?: number; reps?: number; duration_seconds?: number; rir?: number; resistance_kind?: "external_load" | "band" | "bodyweight" | "assistance" | "unknown" }) => {
    const result = await request<{ set?: SetEntry }>(`/api/sessions/${sessionId}/sets`, { method: "POST", body: JSON.stringify(value) });
    if (result.set) return { set: result.set };
    // The idempotency replay response only carries the saved row identity;
    // reload the active session to recover the complete set representation.
    const current = await api.today();
    const saved = current.session?.sets.find((set) => set.routine_item_id === value.routine_item_id && set.round_number === value.round_number);
    if (!saved) throw new ApiError("Saved set could not be loaded", 502);
    return { set: saved };
  },
  saveExerciseNote: (sessionId: string, itemId: string, value: { notes: string | null; expected_revision: number }) =>
    request<{ note: ExerciseFeedback }>(`/api/sessions/${encodeURIComponent(sessionId)}/exercise-notes/${encodeURIComponent(itemId)}`, { method: "PUT", body: JSON.stringify(value) }),
  completeSession: (sessionId: string, value: { perceived_effort: number; notes?: string }) =>
    request<{ session: WorkoutSession }>(`/api/sessions/${sessionId}`, { method: "PATCH", body: JSON.stringify({ status: "completed", ...value }) }),
  abandonSession: (sessionId: string) =>
    request<{ session: WorkoutSession }>(`/api/sessions/${sessionId}`, { method: "PATCH", body: JSON.stringify({ status: "abandoned" }) }),
  plan: () => request<PlanPayload>("/api/plan"),
  week: (weekId: string) => request<{ week: AgentWeekInput; revision?: number }>(`/api/plan/weeks/${encodeURIComponent(weekId)}`),
  drafts: () => request<{ drafts: PlanDraft[] }>("/api/plan/drafts"),
  createDraft: (week: AgentWeekInput, expectedRevision = 0) => request<{ draft: PlanDraft }>("/api/plan/drafts", { method: "POST", body: JSON.stringify({ ...week, expected_revision: expectedRevision }) }),
  updateDraft: (draftId: string, week: AgentWeekInput, expectedRevision?: number) => request<{ draft: PlanDraft }>(`/api/plan/drafts/${encodeURIComponent(draftId)}`, { method: "PUT", body: JSON.stringify({ ...week, ...(expectedRevision === undefined ? {} : { expected_revision: expectedRevision }) }) }),
  publishDraft: (draftId: string, expectedRevision?: number, expectedWeekRevision?: number) => request<{ ok: true; week_id: string; revision?: number }>(`/api/plan/drafts/${encodeURIComponent(draftId)}/publish`, { method: "POST", body: JSON.stringify({ ...(expectedRevision === undefined ? {} : { expected_revision: expectedRevision }), ...(expectedWeekRevision === undefined ? {} : { expected_week_revision: expectedWeekRevision }) }) }),
  tokens: () => request<{ tokens: ApiToken[] }>("/api/tokens"),
  createToken: (name: string, scopes: string[]) => request<{ token: string; credential: ApiToken }>("/api/tokens", { method: "POST", body: JSON.stringify({ name, scopes }) }),
  revokeToken: (tokenId: string) => request<{ ok: true }>(`/api/tokens/${encodeURIComponent(tokenId)}`, { method: "DELETE" }),
  reports: () => request<ReportsPayload>("/api/reports")
};
